import BundleApi from "@yoco/shop-sdk/lib/api/BundleApi"
import CartApi from "@yoco/shop-sdk/lib/api/CartApi"
import OAuthApi from "@yoco/shop-sdk/lib/api/OAuthApi"
import ProductApi from "@yoco/shop-sdk/lib/api/ProductApi"
import SignupsApi from "@yoco/shop-sdk/lib/api/SignupsApi"
import HttpClient from "@yoco/shop-sdk/lib/HttpClient"

import { getUrlFromStoryblokLink, parseStoryblokDate } from "./storyblok"

import { isPurchasableBundle, PurchasableItem } from "@hooks/V2/storyblok"

const http = new HttpClient(`${process.env.GATSBY_SHOP_API_URL}`)
export const shop = {
  carts: new CartApi(http),
  products: new ProductApi(http),
  bundles: new BundleApi(http),
  oauth: new OAuthApi(http),
  signups: new SignupsApi(http),
}

const isBrowser = typeof window !== "undefined"

export const localStorageAvailable = (): boolean => {
  if (!isBrowser) {
    return false
  }
  try {
    localStorage.setItem("testCookie", "testCookie")
    localStorage.removeItem("testCookie")
    return true
  } catch (e) {
    return false
  }
}

export const sessionStorageAvailable = (): boolean => {
  if (!isBrowser) {
    return false
  }
  try {
    sessionStorage.setItem("testCookie", "testCookie")
    sessionStorage.removeItem("testCookie")
    return true
  } catch (e) {
    return false
  }
}

export const fetchCartId = (): string | null => {
  if (localStorageAvailable()) {
    return localStorage.getItem("cartId")
  }
  return null
}

export const storeCartId = (id: string): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("cartId", id)
  }
}

export const itemAlreadyInCart = (item: PurchasableItem): boolean => {
  return isPurchasableBundle(item)
    ? bundleAlreadyInCart(item.bundleCode)
    : productAlreadyInCart(item.sku)
}

export const productAlreadyInCart = (sku: string): boolean => {
  const products = fetchCartProducts()
  const inCart = products?.find((product) => {
    // This is a hotfix for users who already have existing cart items without IDs
    if (!product.id) {
      clearCart()
      return false
    }

    if (!isStoredProduct(product)) return false
    return product.sku === sku
  })

  return inCart ? true : false
}

export const bundleAlreadyInCart = (bundleCode: string): boolean => {
  const products = fetchCartProducts()

  const inCart = products?.find((product) => {
    // This is a hotfix for users who already have existing cart items without IDs
    if (!product.id) {
      clearCart()
      return false
    }

    if (!isStoredBundle(product)) return false

    return product.bundleCode === bundleCode
  })

  return inCart ? true : false
}

export interface StoredItemBase {
  id: string
  quantity: number
  storefront: string
}

export interface StoredProduct extends StoredItemBase {
  sku: string
  type: "product"
}

export interface StoredBundle extends StoredItemBase {
  bundleCode: string
  type: "bundle"
}

export type StoredItemType = "product" | "bundle"

export type StoredItem = StoredProduct | StoredBundle

export const isStoredProduct = (
  storedItem: StoredItem
): storedItem is StoredProduct => {
  return storedItem.type === "product"
}

export const isStoredBundle = (
  storedItem: StoredItem
): storedItem is StoredBundle => {
  return storedItem.type === "bundle"
}

export const fetchCartProducts = ():
  | (StoredBundle | StoredProduct)[]
  | null => {
  if (localStorageAvailable()) {
    const products = localStorage.getItem("cartProducts")
    return products ? JSON.parse(products) : null
  }

  return null
}

export const getProductName = (
  sdkProduct: YocoCom.SdkProduct | null,
  storyblokName: string
): string => {
  return sdkProduct?.short_name || storyblokName
}

export interface ProductPrice {
  price: string
  originalPrice?: string
}

export const getProductPrices = (
  product: YocoCom.SdkProduct | null,
  storyblokPrice: string,
  forceStoryblokPrice: boolean,
  storyblokPromotionPrice?: string
): ProductPrice => {
  if (forceStoryblokPrice || !product) {
    const storyblokPrices: ProductPrice = {
      price: storyblokPromotionPrice ? storyblokPromotionPrice : storyblokPrice,
    }
    if (storyblokPromotionPrice && parseFloat(storyblokPrice) > 0) {
      storyblokPrices["originalPrice"] = storyblokPrice
    }

    return storyblokPrices
  }

  const sdkPrices: ProductPrice = {
    price: product.unit_price,
  }

  if (parseFloat(product.original_unit_price) > 0) {
    sdkPrices["originalPrice"] = product.original_unit_price
  }

  return sdkPrices
}

export const getBundlePrices = (
  bundle: YocoCom.SdkBundle | null,
  storyblokPrice: string,
  forceStoryblokPrice: boolean,
  storyblokPromotionPrice?: string
): ProductPrice => {
  if (forceStoryblokPrice || !bundle) {
    const storyblokPrices: ProductPrice = {
      price: storyblokPromotionPrice ? storyblokPromotionPrice : storyblokPrice,
    }

    if (storyblokPromotionPrice && parseFloat(storyblokPrice) > 0) {
      storyblokPrices["originalPrice"] = storyblokPrice
    }

    return storyblokPrices
  }

  const sdkPrices: ProductPrice = {
    price: bundle.unit_price,
  }

  if (parseFloat(bundle.original_unit_price) > 0) {
    sdkPrices["originalPrice"] = bundle.original_unit_price
  }

  return sdkPrices
}

export const checkPromotion = (
  promotionStart?: string,
  promotionEnd?: string
): boolean => {
  const today = new Date()
  const startDate = promotionStart ? parseStoryblokDate(promotionStart) : null
  const endDate = promotionEnd ? parseStoryblokDate(promotionEnd) : null
  const shouldRenderPromotion =
    startDate && endDate && today >= startDate && today <= endDate

  return !!shouldRenderPromotion
}

export const consolidateProduct = (
  productId: string,
  quantity: number,
  sdkProduct: YocoCom.SdkProduct,
  storyblokProduct: Storyblok.ProductConfig
): YocoCom.ConsolidatedProduct => {
  const productPrices = getProductPrices(
    sdkProduct,
    storyblokProduct.price,
    storyblokProduct.forceStoryblokPrice,
    storyblokProduct.promotionPrice
  )

  const originalPrice = productPrices?.originalPrice
    ? parseFloat(productPrices?.originalPrice)
    : undefined

  const savingsAmount =
    originalPrice && parseFloat(productPrices.price)
      ? originalPrice - parseFloat(productPrices.price)
      : 0

  const showPromotion = checkPromotion(
    storyblokProduct.promotionStartDate,
    storyblokProduct.promotionEndDate
  )

  const showOriginalPrice =
    showPromotion &&
    !!originalPrice &&
    originalPrice - parseFloat(productPrices.price) > 0 &&
    originalPrice > 0

  return {
    id: productId,
    sku: storyblokProduct.sku ?? sdkProduct.sku,
    name: sdkProduct.short_name ?? storyblokProduct.name,
    cartTitle:
      storyblokProduct.overrideCartTitle ||
      sdkProduct.short_name ||
      storyblokProduct.name,
    image: storyblokProduct?.image ?? sdkProduct.image,
    quantity: quantity,
    maxQuantity: sdkProduct.max_quantity_per_cart ?? undefined,
    price: productPrices.price,
    originalPrice: originalPrice?.toString(),
    savingsAmount: savingsAmount,
    detailLink: getUrlFromStoryblokLink(storyblokProduct.detailsLink),
    description: storyblokProduct.shortDescription ?? sdkProduct.description,
    galleryItems: storyblokProduct.galleryItems,
    thirdPartyReviews: sdkProduct.third_party_reviews,
    recommendedProducts: storyblokProduct.recommendedProducts,
    recommendedLabel: storyblokProduct.recommendedLabel,
    showRecommendedProducts: storyblokProduct.showRecommendedProducts,
    promotionStartDate: storyblokProduct.promotionStartDate,
    promotionEndDate: storyblokProduct.promotionEndDate,
    reasonNotAvailable: sdkProduct.reason_not_available,
    priceSideScript: storyblokProduct.priceSuffixLabel,
    priceSubScript: storyblokProduct.priceInfoText,
    storefront: storyblokProduct.storefront,
    onPromotion: showOriginalPrice,
    type: "product",
  }
}

export const consolidateBundle = (
  bundleId: string,
  quantity: number,
  sdkBundle: YocoCom.SdkBundle,
  storyblokBundle: Storyblok.BundleConfig
): YocoCom.ConsolidatedBundle => {
  const bundlePrices = getBundlePrices(
    sdkBundle,
    storyblokBundle.originalPrice?.toString() ||
      storyblokBundle.price.toString(),
    storyblokBundle.forceStoryblokPrice,
    storyblokBundle.price.toString()
  )

  const originalPrice = bundlePrices?.originalPrice
    ? parseFloat(bundlePrices?.originalPrice)
    : undefined

  const savingsAmount =
    originalPrice && parseFloat(bundlePrices.price)
      ? originalPrice - parseFloat(bundlePrices.price)
      : 0

  const showPromotion = checkPromotion(
    storyblokBundle.promotionStartDate,
    storyblokBundle.promotionEndDate
  )
  const showOriginalPrice =
    showPromotion &&
    !!originalPrice &&
    originalPrice - parseFloat(bundlePrices.price) > 0 &&
    originalPrice > 0

  return {
    id: bundleId,
    bundleCode: storyblokBundle.code ?? sdkBundle.bundle_code,
    name: sdkBundle.short_name ?? storyblokBundle.name,
    cartTitle:
      storyblokBundle.overrideCartTitle ||
      sdkBundle.short_name ||
      storyblokBundle.name,
    image: storyblokBundle?.image ?? sdkBundle.image,
    quantity: quantity,
    maxQuantity: sdkBundle.max_quantity_per_cart ?? undefined,
    price: bundlePrices.price,
    originalPrice: originalPrice?.toString(),
    priceSideScript: storyblokBundle.priceSidescript,
    priceSubScript: storyblokBundle.priceSubscript,
    savingsAmount: savingsAmount,
    detailLink: getUrlFromStoryblokLink(storyblokBundle.shopPage),
    description: storyblokBundle.shortDescription ?? sdkBundle.description,
    galleryItems: storyblokBundle.galleryItems,
    recommendedProducts: storyblokBundle.recommendedProducts,
    recommendedLabel: storyblokBundle.recommendedLabel,
    showRecommendedProducts: storyblokBundle.showRecommendedProducts,
    promotionStartDate: storyblokBundle.promotionStartDate,
    promotionEndDate: storyblokBundle.promotionEndDate,
    reasonNotAvailable: sdkBundle.reason_not_available,
    storefront: storyblokBundle.storefront,
    onPromotion: showOriginalPrice,
    type: "bundle",
  }
}

export const storeCartProducts = (products: StoredItemBase[]): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("cartProducts", JSON.stringify(products))
  }
}

export const fetchCartPromoCode = (): string | null => {
  if (localStorageAvailable()) {
    return localStorage.getItem("promoCode")
  }
  return null
}

export const storeCartPromoCode = (promoCode: string): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("promoCode", promoCode)
  }
}

export const clearCartPromoCode = (): void => {
  if (localStorageAvailable()) {
    localStorage.removeItem("promoCode")
  }
}

export const clearCartId = (): void => {
  if (localStorageAvailable()) {
    localStorage.removeItem("cartId")
  }
}

export const clearCart = (): void => {
  if (localStorageAvailable()) {
    localStorage.removeItem("cartId")
    localStorage.removeItem("cartProducts")
    localStorage.removeItem("promoCode")
  }
}

export const humanizeAddressText = (
  address: YocoCom.ShippingAddress
): string => {
  const addressList = []

  if (address.country) {
    addressList.push(address.country)
  }
  if (address.street) {
    addressList.push(address.street)
  }
  if (address.suburb) {
    addressList.push(address.suburb)
  }
  if (address.city) {
    addressList.push(address.city)
  }
  if (address.province) {
    addressList.push(address.province)
  }
  if (address.postalCode) {
    addressList.push(address.postalCode)
  }

  return addressList.join(", ")
}

export const isShopEnabled = (): boolean => {
  // This fetches a static setting determined at build time

  if (process.env.GATSBY_IS_SHOP_ENABLED === "true") {
    return true
  } else {
    return false
  }
}

export const isConsolidatedProduct = (
  cartItem: YocoCom.ConsolidatedCartItem
): cartItem is YocoCom.ConsolidatedProduct => {
  return cartItem.type === "product"
}

export const isConsolidatedBundle = (
  cartItem: YocoCom.ConsolidatedCartItem
): cartItem is YocoCom.ConsolidatedBundle => {
  return cartItem.type === "bundle"
}
